import React, { useEffect, useRef, useState } from "react";
import { Play, SkipForward, ExternalLink, AlertCircle } from "lucide-react";
import type { QueueSong } from "../../types";

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

interface YouTubePlayerProps {
  currentSong: QueueSong | null;
  onSongEnd: () => void;
  isHost: boolean;
}

export const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  currentSong,
  onSongEnd,
  isHost,
}) => {
  const playerRef = useRef<any>(null);
  const isAdvancingRef = useRef<boolean>(false);
  const [apiReady, setApiReady] = useState(false);
  const [playerError, setPlayerError] = useState<string | null>(null);

  // Load YouTube IFrame API once
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      setApiReady(true);
      return;
    }

    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      setApiReady(true);
    };
  }, []);

  // Initialize or update player when currentSong or apiReady changes
  useEffect(() => {
    if (!apiReady || !currentSong) {
      return;
    }

    // Reset advancing guard for the new track
    isAdvancingRef.current = false;

    if (!currentSong.embeddable) {
      setPlayerError("The owner of this video has disabled playback on other websites.");
      playerRef.current = null;
      return;
    }

    setPlayerError(null);
    const videoId = currentSong.videoId;

    const createOrLoadPlayer = () => {
      const container = document.getElementById("youtube-player-container");
      if (!container) return;

      if (!playerRef.current) {
        playerRef.current = new window.YT.Player("youtube-player-container", {
          height: "100%",
          width: "100%",
          videoId: videoId,
          playerVars: {
            autoplay: 1,
            controls: 1,
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
            enablejsapi: 1,
            origin: window.location.origin,
          },
          events: {
            onStateChange: (event: any) => {
              // YT.PlayerState.ENDED is 0
              if (event.data === 0) {
                if (!isAdvancingRef.current) {
                  isAdvancingRef.current = true;
                  onSongEnd();
                }
              }
            },
            onError: (event: any) => {
              console.warn("YouTube Player error event:", event.data);
              playerRef.current = null;
              if (event.data === 101 || event.data === 150) {
                setPlayerError("The video owner has restricted embedded playback on external sites.");
              } else if (event.data === 100 || event.data === 2) {
                setPlayerError("Video is unavailable or not found.");
              } else {
                setPlayerError("Playback unavailable in embedded player.");
              }
            },
          },
        });
      } else {
        try {
          playerRef.current.loadVideoById(videoId);
        } catch (err) {
          console.warn("Error loading video in existing player, re-creating:", err);
          playerRef.current = null;
          setTimeout(createOrLoadPlayer, 100);
        }
      }
    };

    const timer = setTimeout(createOrLoadPlayer, 50);
    return () => clearTimeout(timer);
  }, [apiReady, currentSong, onSongEnd]);

  if (!currentSong) {
    return (
      <div className="w-full aspect-video bg-black/60 rounded-3xl border border-border flex flex-col items-center justify-center p-6 text-center space-y-3">
        <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-primary animate-pulse">
          <Play className="w-8 h-8 ml-1" />
        </div>
        <div>
          <h3 className="text-2xl font-bold font-heading text-text-main">Queue is Empty</h3>
          <p className="text-xs text-text-muted max-w-xs mx-auto">
            Scan the QR code with your phone or search for songs in the controller tab to start singing!
          </p>
        </div>
      </div>
    );
  }

  // Non-embeddable or playback error fallback view
  if (!currentSong.embeddable || playerError) {
    return (
      <div className="w-full aspect-video bg-black/90 rounded-3xl border border-yellow-500/40 flex flex-col items-center justify-center p-6 text-center space-y-4 shadow-2xl animate-fade-in">
        <div className="w-14 h-14 rounded-2xl bg-yellow-500/20 text-yellow-400 flex items-center justify-center">
          <AlertCircle className="w-8 h-8" />
        </div>
        <div className="space-y-1.5 max-w-md">
          <h3 className="text-base sm:text-lg font-bold text-text-main leading-tight line-clamp-2">
            {currentSong.title}
          </h3>
          <p className="text-xs text-yellow-300/90 font-medium">
            {playerError || "Playback on other websites has been disabled by the video owner."}
          </p>
          <p className="text-[11px] text-text-muted">
            You can still sing along by opening it in a YouTube tab, or skip to the next song.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
          <a
            href={`https://www.youtube.com/watch?v=${currentSong.videoId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="py-3 px-5 rounded-2xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold flex items-center gap-2 transition-all shadow-lg shadow-red-600/30 cursor-pointer"
          >
            <ExternalLink className="w-4 h-4" />
            <span>Open in YouTube Tab</span>
          </a>

          {isHost && (
            <button
              onClick={() => {
                if (!isAdvancingRef.current) {
                  isAdvancingRef.current = true;
                  onSongEnd();
                }
              }}
              className="py-3 px-5 rounded-2xl bg-card border border-border hover:border-primary text-text-main text-xs font-bold flex items-center gap-2 transition-all cursor-pointer"
            >
              <SkipForward className="w-4 h-4 text-primary" />
              <span>Next Song</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full aspect-video rounded-3xl overflow-hidden border border-border bg-black relative shadow-2xl">
      <div id="youtube-player-container" className="w-full h-full" />
    </div>
  );
};
